import axios from "axios";
import Firebase from "firebase/app";
import "firebase/auth";

const state = {
  tasksData: [],
  fetchingData: true,
  tasksSettingsLoaded: false,
  taskDialog: false,
  progressData: { total: 100, done: 0, doneByUser: 0, gif: { show: false } },
  maxWords: null,
  savedIndex: null,
  userInfo: {},
  fetchingMessageDelay: 1,
  fetchingGifMessageDelay: 6000,
  successMessageDelay: 2000,
  selectedTasksIndices: [],
  uploadingLabels: "idle",
  labelsSinceLastCongrats: 0,
  minLabelsForCongrats: 50,
  classifierNames: [],
  attributionsView: "show all",
  attributionsViewTypes: ["show all", "on demand", "replicated"],
  selectedDataset: null,
  selectionMethods: { user: [], admin: [] },
  classifiers: [],
  classifiersConfigs: {},
  datasets: [],
  labelingMode: "labelIncorrect", // possible values: 'ignoreUnlabeled', 'labelIncorrect'
  selectionMethod: null, // possible values:  'data at random',  'data confusing the classifier'
  stats: {},
  filterTokens: [],
  filterClassifierName: null,
  thresholding: {
    fine_toxicity: { multilabel: true, threshold: 0.5 },
    emotion_ensemble: { multilabel: false, threshold: 0.25 },
    sentiment: { multilabel: false, threshold: 0.4 },
    profanity_en: { multilabel: false, positiveClass: "profane", threshold: 0.7 },
    profanity_fr: { multilabel: false, positiveClass: "profane", threshold: 0.7 },
    profanity_de: { multilabel: false, positiveClass: "profane", threshold: 0.7 },
    toxicity: { multilabel: false, positiveClass: "toxic", threshold: 0.7 },
    hate: { multilabel: false, positiveClass: "hate", threshold: 0.7 },
    hate_fr_trans: { multilabel: false, positiveClass: "hate", threshold: 0.7 },
    hate_de_trans: { multilabel: false, positiveClass: "hate", threshold: 0.7 },
    hate_de_all: { multilabel: false, positiveClass: "hate", threshold: 0.7 },
    unbiased_toxicity: { multilabel: false, positiveClass: "toxic", threshold: 0.7 },
    severe_toxicity: {
      multilabel: false,
      positiveClass: "severe_toxicity",
      threshold: 0.5,
      parent: "unbiased_toxicity"
    },
    obscene: { multilabel: false, positiveClass: "obscene", threshold: 0.5, parent: "unbiased_toxicity" },
    identity_attack: {
      multilabel: false,
      positiveClass: "identity_attack",
      threshold: 0.5,
      parent: "unbiased_toxicity"
    },
    identity: { multilabel: true, threshold: 0.7, parent: "unbiased_toxicity" },
    insult: { multilabel: false, positiveClass: "insult", threshold: 0.5, parent: "unbiased_toxicity" },
    threat: { multilabel: false, positiveClass: "threat", threshold: 0.5, parent: "unbiased_toxicity" },
    sexual_explicit: {
      multilabel: false,
      positiveClass: "sexual_explicit",
      threshold: 0.5,
      parent: "unbiased_toxicity"
    },
    aggression: { multilabel: false, positiveClass: "aggressive", threshold: 0.7, parent: "unbiased_toxicity" }
  }
};

const getters = {
  tasksSettingsLoaded: state => {
    return state.tasksSettingsLoaded;
  },
  stats: state => {
    return state.stats;
  },
  filterTokens: state => {
    return state.filterTokens;
  },
  filterClassifierName: state => {
    return state.filterClassifierName;
  },
  attributionsViewTypes: state => {
    return state.attributionsViewTypes;
  },
  classifiersConfigs: state => {
    return state.classifiersConfigs;
  },
  debuggerMode: (state, getters, rootState) => {
    return rootState.route.name === "dataDebugger";
  },
  attributionsView: state => {
    return state.attributionsView;
  },
  selectedDataset: state => {
    return state.selectedDataset;
  },
  tasksData: state => {
    return state.tasksData;
  },
  progressData: state => {
    return state.progressData;
  },
  selectionMethods: state => {
    return state.selectionMethods;
  },
  classifiers: state => {
    return state.classifiers;
  },
  datasets: state => {
    return state.datasets;
  },
  labelsSinceLastCongrats: state => {
    return state.labelsSinceLastCongrats;
  },
  maxWords: state => {
    return state.maxWords;
  },
  taskDialog: state => {
    return state.taskDialog;
  },
  fetchingData: state => {
    return state.fetchingData;
  },
  savedIndex: state => {
    return state.savedIndex;
  },
  uploadingLabels: state => {
    return state.uploadingLabels;
  },
  classifierNames: state => {
    return state.classifierNames;
  },
  selectedTasksIndices: state => {
    return state.selectedTasksIndices;
  },
  labelingMode: state => {
    return state.labelingMode;
  },
  userInfo: state => {
    return state.userInfo;
  },
  selectionMethod: state => {
    return state.selectionMethod;
  }
};

const mutations = {
  setTasksData: (state, payload) => {
    state.tasksData = payload;
  },
  setFilterTokens: (state, payload) => {
    state.filterTokens = payload;
  },
  setFilterClassifierName: (state, payload) => {
    state.filterClassifierName = payload;
  },
  setAttributionsView: (state, payload) => {
    state.attributionsView = payload;
  },
  setTasksSettingsLoaded: (state, payload) => {
    state.tasksSettingsLoaded = payload;
  },
  setMaxWords: (state, payload) => {
    state.maxWords = payload;
  },
  setStats: (state, payload) => {
    state.stats = payload;
  },
  removeTask: (state, payload) => {
    state.tasksData.splice(payload, 1);
  },
  removeAllTasks: state => {
    state.tasksData = [];
  },
  setProgressData: (state, payload) => {
    state.progressData = payload;
  },
  setSelectedDataset: (state, payload) => {
    state.selectedDataset = payload;
  },
  setUserInfo: (state, payload) => {
    state.userInfo = payload;
  },
  setTaskDialog: (state, payload) => {
    state.taskDialog = payload;
  },
  setDatasets: (state, payload) => {
    state.datasets = payload;
  },
  setClassifiers: (state, payload) => {
    state.classifiers = payload;
  },
  setSelectionMethods: (state, payload) => {
    state.selectionMethods = payload;
  },
  setFetchingData: (state, payload) => {
    state.fetchingData = payload;
  },
  setSavedIndex: (state, payload) => {
    state.savedIndex = payload;
  },
  setClassifierNames: (state, payload) => {
    state.classifierNames = payload;
  },
  setClassifiersConfigs: (state, payload) => {
    state.classifiersConfigs = payload;
  },
  setUploadingLabels: (state, payload) => {
    state.uploadingLabels = payload;
  },
  setLabelsSinceLastCongrats: (state, payload) => {
    state.labelsSinceLastCongrats = payload;
  },
  setSelectedTasksIndices: (state, payload) => {
    state.selectedTasksIndices = payload;
  },
  setSelectionMethod: (state, payload) => {
    state.selectionMethod = payload;
  },
  updateTaskStatus: (state, payload) => {
    state.tasksData[payload.index].status = payload.status;
  },
  chooseMaxLabel(state, payload) {
    let initial = payload["initial"];
    let taskData = payload["taskData"];
    let classifierNames = payload["classifierNames"] || state.classifierNames;
    let positiveClassResults = {};
    let MIN_OPACITY = 0.2;

    let thresholding = function(classifier) {
      if (classifier in state.thresholding) {
        return state.thresholding[classifier];
      } else {
        return { multilabel: false, threshold: 0.5 };
      }
    };
    let passesThreshold = function(l, classifier) {
      let multilabel = thresholding(classifier).multilabel;
      return (
        (multilabel && l.score > 0.5) ||
        (!multilabel &&
          l.score === taskData.predictions[classifier].max_score &&
          l.score > thresholding(classifier).threshold)
      );
    };
    for (let classifier of classifierNames) {
      if (classifier in taskData.predictions) {
        for (let j = 0; j < taskData.predictions[classifier].preds.length; j++) {
          let l = taskData.predictions[classifier].preds[j];
          let parentClassifier = thresholding(classifier).parent;

          if (passesThreshold(l, classifier)) {
            l.chosen = true;
            let positiveColor = "#FFECB3";
            let opacity = 1;
            positiveClassResults[classifier] = false;
            let positiveClass = thresholding(classifier).positiveClass;

            if (positiveClass) {
              if (l.label === positiveClass) {
                if (!parentClassifier || positiveClassResults[parentClassifier]) {
                  positiveColor = "#EF9A9A";
                  positiveClassResults[classifier] = true;
                } else {
                  positiveColor = "#e0e0e0";
                }
              }
            } else {
              if (parentClassifier && positiveClassResults[parentClassifier]) {
                positiveColor = "#EF9A9A";
              }
            }

            if (positiveColor === "#e0e0e0") {
              opacity = MIN_OPACITY;
            }

            l.style = {
              "background-color": initial
                ? state.labelingMode === "labelIncorrect"
                  ? positiveColor
                  : positiveColor
                : "#a5d6a7",
              opacity: opacity
            };
          } else {
            l.chosen = false;
            l.style = {
              opacity: MIN_OPACITY,
              "background-color": "#e0e0e0"
            };
          }
        }
      }
    }
  }
};
const notInArr = function notInArr(item) {
  return this.indexOf(item) === -1;
};

function authenticate() {
  let useFirebase = false;
  if (useFirebase) {
    return Firebase.auth().currentUser.getIdToken(true);
  } else {
    return new Promise((resolve, reject) => {
      resolve(
        "eyJhbGciOiJSUzI1NiIsImtpZCI6IjdkMmY5ZjNmYjgzZDYzMzc0OTdiNmY3Y2QyY2ZmNGRmYTVjMmU4YjgiLCJ0eXAiOiJKV1QifQ.eyJpc3MiOiJodHRwczovL3NlY3VyZXRva2VuLmdvb2dsZS5jb20vdGV4dGxhYmVsLWE5MDVkIiwiYXVkIjoidGV4dGxhYmVsLWE5MDVkIiwiYXV0aF90aW1lIjoxNTU0NjUyMzczLCJ1c2VyX2lkIjoiTWxRM1hBUml3R2ZkUUpldXZGWnlSQ1NWREVGMiIsInN1YiI6Ik1sUTNYQVJpd0dmZFFKZXV2Rlp5UkNTVkRFRjIiLCJpYXQiOjE1NTQ2NTIzNzgsImV4cCI6MTU1NDY1NTk3OCwiZW1haWwiOiJtYXRoaWV1QHByaXZhdGVseS5ldSIsImVtYWlsX3ZlcmlmaWVkIjpmYWxzZSwiZmlyZWJhc2UiOnsiaWRlbnRpdGllcyI6eyJlbWFpbCI6WyJtYXRoaWV1QHByaXZhdGVseS5ldSJdfSwic2lnbl9pbl9wcm92aWRlciI6InBhc3N3b3JkIn19.oTvPqGTnjrL6aVansIKfFhYfzdm3FMLKA2jyCpkBq2Cj7zsuchWNSjfnYV_R0VeIhXfyW9HN9rdxCEufyPXKiNyFfqsaT8R8d4Curw0IDtNS7ZMOj66XIqttjkHuJqabFqwLu8N7nTIH2-loGfRUSc1uNRgyiKWaDAF6jHtyRpTU2AiBsMxHZQvWw19FN46-TGojd120QYcCSU3NQqevRN6ZbCjT2ata5CkwhPalj_WTMxo75EpOPVTz_QH_8mVy73-35BDba0_wVqT4IGA7K6WPuI3di32_NF1uqT28AmLEueXnHee43ZTfjr1EPdzr2TKQ-9JqHgWhj_TfopWh0g"
      );
    });
  }
}

function addCheckedSubset(current, add) {
  if (!current.length) return add;

  // Add only messages which are not already checked
  return current.concat(add.filter(notInArr, current));
}

function removeCheckedSubset(current, remove) {
  // Return only messages which are not in subset
  return current.filter(notInArr, remove);
}

const actions = {
  fetchNewTasks: ({ commit, state, rootState, getters }) => {
    let addGif = false;
    if (state.labelsSinceLastCongrats >= state.minLabelsForCongrats) {
      addGif = true;
    }
    commit("setFetchingData", true);
    return new Promise((resolve, reject) => {
      // we get the token that can be verified at the server
      authenticate()
        .then(function(idToken) {
          // Send token to backend via HTTPS
          axios
            .post("/data", {
              classifier_names: state.classifierNames,
              selection_method: rootState.route.name === "dataDebugger" ? "data debugger" : state.selectionMethod,
              max_words: state.maxWords,
              addGif: addGif,
              dataset: state.selectedDataset,
              idToken: idToken
            })
            .then(function(results) {
              commit("setProgressData", results.data.progress);
              commit("setStats", results.data.stats);

              // sort labels within each classifier in alphabetical order
              for (let i = 0; i < results.data.taskList.length; i++) {
                results.data.taskList[i].status = "unknown";
                for (let classifier of state.classifierNames) {
                  results.data.taskList[i].predictions[classifier].preds.sort(function(a, b) {
                    if (a.label < b.label) {
                      return -1;
                    }
                    if (a.label > b.label) {
                      return 1;
                    }
                    return 0;
                  });
                }
              }
              commit("setTasksData", results.data.taskList);
              if (!getters.debuggerMode) {
                for (let i = 0; i < state.tasksData.length; i++) {
                  commit("chooseMaxLabel", {
                    initial: true,
                    taskData: state.tasksData[i]
                  });
                }
              }
              if (addGif) {
                setTimeout(() => {
                  commit("setFetchingData", false);
                  commit("setLabelsSinceLastCongrats", 0);
                  resolve();
                }, state.fetchingGifMessageDelay);
              } else {
                setTimeout(() => {
                  commit("setFetchingData", false);
                  resolve();
                }, state.fetchingMessageDelay);
              }
            })
            .catch(function(e) {
              reject(new Error("cannot fetch tasks: ") + e);
            });
        })
        .catch(function() {
          // Handle error
        });
    });
  },
  setClassifiers: ({ commit }, payload) => {
    commit("setClassifiers", payload);
  },
  setSavedIndex: ({ commit }, payload) => {
    commit("setSavedIndex", payload);
  },
  removeAllTasks: ({ commit, dispatch }) => {
    commit("removeAllTasks");
    dispatch("fetchNewTasks", {});
  },
  removeTask: ({ commit, state, dispatch }, payload) => {
    commit("removeTask", payload);
    if (state.tasksData.length === 0) {
      dispatch("fetchNewTasks", {});
    }
  },
  setSelectedDataset: ({ commit }, payload) => {
    commit("setSelectedDataset", payload);
  },
  setFilterClassifierName: ({ commit }, payload) => {
    commit("setFilterClassifierName", payload);
  },
  setFilterTokens: ({ commit }, payload) => {
    commit("setFilterTokens", payload);
  },
  setUploadingLabels: ({ commit }, payload) => {
    commit("setUploadingLabels", payload);
  },
  setClassifierNames: ({ commit }, payload) => {
    commit("setClassifierNames", payload);
  },
  setClassifiersConfigs: ({ commit }, payload) => {
    commit("setClassifiersConfigs", payload);
  },
  setLabelsSinceLastCongrats: ({ commit }, payload) => {
    commit("setLabelsSinceLastCongrats", payload);
  },
  setSelectionMethod: ({ commit }, payload) => {
    commit("setSelectionMethod", payload);
  },
  chooseMaxLabel: ({ commit }, payload) => {
    commit("chooseMaxLabel", payload);
  },
  setMaxWords: ({ commit }, payload) => {
    commit("setMaxWords", payload);
  },
  updateStats: ({ commit }, payload) => {
    commit("updateStats", payload);
  },
  setTaskDialog: ({ commit }, payload) => {
    commit("setTasksData", []);
    commit("setTaskDialog", payload);
  },
  setTasksSettingsLoaded: ({ commit }, payload) => {
    commit("setTasksSettingsLoaded", payload);
  },
  setAttributionsView: ({ commit }, payload) => {
    commit("setAttributionsView", payload);
  },
  updateSelections: ({ commit, state }, payload) => {
    let index = payload.index;
    let savedIndex = payload.savedIndex;

    // Get the subset of the message list between the
    // two indicies.
    let arr = [...Array(state.tasksData.length).keys()];

    const subset = arr.slice(Math.min(index, savedIndex), Math.max(index, savedIndex) + 1);
    // Determine the operation based on the checked state
    // of the clicked checkbox.
    const toggleFn = state.tasksData[index].status === "checked" ? removeCheckedSubset : addCheckedSubset;

    let selectedTasksIndices = [];
    for (let i = 0; i < state.tasksData.length; i++) {
      if (state.tasksData[i].status === "checked") {
        selectedTasksIndices.push(i);
      }
    }

    selectedTasksIndices = toggleFn(selectedTasksIndices, subset);
    for (let i = 0; i < state.tasksData.length; i++) {
      if (selectedTasksIndices.indexOf(i) >= 0) {
        if (state.tasksData[i].status == "unknown") {
          commit("chooseMaxLabel", {
            initial: false,
            taskData: state.tasksData[i]
          });
        }
        commit("updateTaskStatus", { index: i, status: "checked" });
      } else {
        commit("updateTaskStatus", { index: i, status: "unknown" });
      }
    }
  },
  uploadLabels: ({ commit, state, dispatch }) => {
    commit("setUploadingLabels", "pending");

    return new Promise((resolve, reject) => {
      let tasksToUpload = [];
      // prepare the data to upload
      for (let i = 0; i < state.tasksData.length; i++) {
        const task = state.tasksData[i];

        if (state.labelingMode === "ignoreUnlabeled") {
          if (task.status === "checked") {
            tasksToUpload.push(task);
          }
        } else if (state.labelingMode === "labelIncorrect") {
          tasksToUpload.push(task);
        }
      }

      authenticate()
        .then(function(idToken) {
          // Send token to backend via HTTPS

          let payload = {
            tasks: tasksToUpload,
            classifier_names: state.classifierNames,
            idToken: idToken,
            dataset: state.selectedDataset
          };

          axios
            .post("/labels", payload)
            .then(function() {
              commit("setLabelsSinceLastCongrats", state.labelsSinceLastCongrats + tasksToUpload.length);
              dispatch("fetchNewTasks", {});

              commit("setUploadingLabels", "success");
              setTimeout(() => {
                commit("setUploadingLabels", "idle");
                resolve();
              }, state.successMessageDelay);

              resolve();
            })
            .catch(function() {
              commit("setUploadingLabels", "error");

              reject(new Error("cannot upload labels"));
            });
        })
        .catch(function() {
          // Handle error
        });
    });
  },
  getUserStats: ({ commit }) => {
    return new Promise((resolve, reject) => {
      // we get the token that can be verified at the server
      authenticate()
        .then(function(idToken) {
          // Send token to backend via HTTPS
          axios
            .get("/userInfo", {
              params: { idToken: idToken }
            })
            .then(function(results) {
              commit("setUserInfo", results.data);
              resolve(results);
            })
            .catch(function(error) {
              if (error) {
                reject(new Error("cannot fetch user info"));
              }
            });
        })
        .catch(function() {
          // Handle error
        });
    });
  },
  getTasksSettings: ({ commit }) => {
    return new Promise((resolve, reject) => {
      // we get the token that can be verified at the server
      authenticate()
        .then(function(idToken) {
          console.log(idToken);
          // Send token to backend via HTTPS
          axios
            .get("/tasksSettings", {
              params: { idToken: idToken, src: "showroom" }
            })
            .then(function(results) {
              commit("setSelectionMethods", results.data.selectionMethods);
              commit("setSelectionMethod", results.data.selectionMethod);
              commit("setClassifiers", results.data.classifiers);
              commit("setClassifierNames", results.data.classifierNames);
              commit("setDatasets", results.data.datasets);
              commit("setSelectedDataset", results.data.selectedDataset);
              commit("setClassifiersConfigs", results.data.classifiersConfigs);
              resolve();
            })
            .catch(function(error) {
              if (error) {
                reject(new Error("cannot fetch task settings"));
              }
            });
        })
        .catch(function() {
          // Handle error
        });
    });
  },
  classifyTexts: ({ commit, state }, payload) => {
    return new Promise((resolve, reject) => {
      // we get the token that can be verified at the server
      authenticate()
        .then(function(idToken) {
          // Send token to backend via HTTPS
          axios
            .post("/text", {
              classifiersConfigs: state.classifiersConfigs,
              texts: payload.texts,
              computeAttributions: payload.computeAttributions,
              idToken: idToken
            })
            .then(function(results) {
              resolve(results);
            })
            .catch(function(error) {
              if (error) {
                reject(new Error("cannot classify texts"));
              }
            });
        })
        .catch(function() {
          // Handle error
        });
    });
  },
  addTexts: ({ commit }, payload) => {
    return new Promise((resolve, reject) => {
      // we get the token that can be verified at the server
      authenticate()
        .then(function(idToken) {
          // Send token to backend via HTTPS
          axios
            .post("/addTexts", {
              texts: payload.texts,
              collection: payload.collection,
              idToken: idToken
            })
            .then(function(results) {
              resolve(results);
            })
            .catch(function(error) {
              if (error) {
                reject(new Error("cannot add texts"));
              }
            });
        })
        .catch(function() {
          // Handle error
        });
    });
  },
  addSwissGermanTexts: ({ commit }, payload) => {
    return new Promise((resolve, reject) => {
      // we get the token that can be verified at the server
      authenticate()
        .then(function(idToken) {
          // Send token to backend via HTTPS
          axios
            .post("/feedback", {
              text: payload.text,
              collection: payload.collection,
              label: payload.label,
              idToken: idToken
            })
            .then(function(results) {
              resolve(results);
            })
            .catch(function(error) {
              if (error) {
                reject(new Error("cannot add texts"));
              }
            });
        })
        .catch(function() {
          // Handle error
        });
    });
  }
};

export default {
  state,
  mutations,
  actions,
  getters,
  namespaced: true
};
